import { useEffect, useMemo, useReducer, useState } from 'react'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import {
  PhoneIcon,
  MailIcon,
  MarketMapIcon,
  SparklesIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  CalendarIcon,
  CheckSquareIcon,
  TextIcon,
} from '../components/icons.jsx'
import { StatusPill } from '../components/DataTable.jsx'
import LeadTimelineView from '../components/LeadTimelineView.jsx'
import {
  recordView,
  recordOutcome,
  getIntel,
  calibration,
  calibratedScore,
  OUTCOME_TYPES,
  addNote,
  updateNote,
  deleteNote,
} from '../lib/intel.js'
import { siteUrl } from '../lib/companyMeta.js'
import { isRowEnriched, subscribeEnriched, isEnriching } from '../lib/enrichedLeads.js'
import { runEnrichment, resumeEnrichment, enrichmentRunning } from '../lib/enrichRunner.js'
import { loadActiveBatch } from '../lib/enrich.js'
import { logEvent } from '../lib/events.js'
import { openCompose } from '../lib/compose.js'
import LeadResearch, { ResearchTeaser, EnrichedContact } from '../components/LeadResearch.jsx'
import { getEnrichment, subscribeEnrichment, reenrichState } from '../lib/enrichment.js'
import { enrichLead } from '../lib/enrich.js'
import { useAuth } from '../auth.jsx'
import { useRbac } from '../lib/rbac.js'
import {
  hubspotStatus,
  hubspotMatch,
  hubspotCompany,
  leadLookup,
  matchVerdict,
  syncAge,
  formatDay,
  formatDayTime,
  formatAmount,
  formatCount,
  humanizeEnum,
  isOpenDeal,
  isOpenTask,
  isOverdue,
} from '../lib/hubspot.js'
import {
  paymentLookup,
  fetchLeadPayments,
  formatMoney,
  fromUnix,
  subscriptionPrice,
  subscriptionWhen,
  summarizePayments,
  hubspotBilling,
  PAYMENT_STATUS,
  SUBSCRIPTION_STATUS,
  INVOICE_STATUS,
} from '../lib/payments.js'

// Lead detail in the "experiment page" anatomy: breadcrumb → title + stage +
// actions → a labeled Overview rail on the left → a tabbed main column
// (Details / Activity) whose lead card carries a left accent bar. A lead opens
// from the leads list, which hands the full row over via router state; on a
// cold deep-link we fall back to the intel snapshot, else a graceful notice.
import {
  CATEGORIES,
  CATEGORY_KEYS,
  CATEGORY_LABEL as CATEGORY_LABELS,
  DEFAULT_CATEGORY_PATH,
  categoryPath,
} from '../lib/categories.js'
import { useManifest } from '../lib/data.jsx'
import { getCachedCompanies, syncCategory } from '../lib/leadsCache.js'
import { listManualLeads } from '../lib/manualLeads.js'


const STAGE_TONE = { replied: 'info', meeting: 'purple', won: 'success', lost: 'danger' }

// The directory's row key — the same derivation as the table's keyOf and the
// rowKey below, so a deep link's key finds exactly the row the table linked.
const keyOfRow = (r) => String(r?.company_id || r?.profile_link || r?.name || '')

function formatPhone(raw) {
  if (!raw) return ''
  const digits = String(raw).replace(/\D/g, '').replace(/^1(?=\d{10}$)/, '')
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
  }
  return raw
}

function hostname(url) {
  try {
    return new URL(url).hostname.replace(/^www\./, '')
  } catch {
    return url
  }
}

function splitList(value) {
  return String(value || '')
    .split(';')
    .map((s) => s.trim())
    .filter(Boolean)
}

// Sales-intel fields arrive as free text: usually one item per line, sometimes
// a single semicolon-packed line (the packed-list idiom used elsewhere).
function intelLines(value) {
  const lines = String(value || '')
    .split(/\r?\n/)
    .map((s) => s.trim())
    .filter(Boolean)
  if (lines.length === 1 && lines[0].includes(';')) return splitList(lines[0])
  return lines
}

// Multi-line intel values render as a line-broken list; single values inline.
function IntelLines({ value }) {
  const lines = intelLines(value)
  if (lines.length === 0) return <span className="text-faint">—</span>
  if (lines.length === 1) return <>{lines[0]}</>
  return (
    <div className="flex flex-col gap-y-1">
      {lines.map((l, i) => (
        <div key={i}>{l}</div>
      ))}
    </div>
  )
}

function initialsOf(name) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean)
  if (!parts.length) return '—'
  return (parts[0][0] + (parts[1]?.[0] || '')).toUpperCase()
}

// The packed `contacts` field is a flat run of `name=…; role=…; phone=…` groups;
// a new `name=` token starts a new contact.
function parseContacts(raw) {
  const out = []
  let cur = null
  for (const tok of String(raw || '').split(';')) {
    const eq = tok.indexOf('=')
    if (eq === -1) continue
    const key = tok.slice(0, eq).trim()
    const val = tok.slice(eq + 1).trim()
    if (key === 'name') {
      cur = { name: val, role: '', phone: '' }
      out.push(cur)
    } else if (cur) {
      // Enrichment appends apollo_email / apollo_title / apollo_linkedin /
      // apollo_location to the same packed format — keep whatever is there.
      cur[key] = val
    }
  }
  return out.filter((c) => c.name)
}

// Merge the mirrored HubSpot contacts into the lead's own (imported +
// AI-enriched) contact list, so the Contacts card shows one deduped roster
// instead of the same people split across two panels. Dedupe by email first
// (the strong key), then by name. A contact in BOTH keeps every imported field
// (role, phone, apollo_* enrichment) and only *fills gaps* from HubSpot —
// nothing the sheet gave us is ever overwritten. A HubSpot-only contact is
// added. Everyone touched by the mirror is flagged `_hs` for the badge.
const _normEmail = (e) => String(e || '').trim().toLowerCase()
const _normName = (n) => String(n || '').trim().toLowerCase().replace(/\s+/g, ' ')
function mergeContacts(local, hs) {
  const out = []
  const byEmail = new Map()
  const byName = new Map()
  const index = (c) => {
    const e = _normEmail(c.email || c.apollo_email)
    if (e) byEmail.set(e, c)
    const n = _normName(c.name)
    if (n) byName.set(n, c)
  }
  for (const c of local || []) {
    const copy = { ...c }
    out.push(copy)
    index(copy)
  }
  for (const h of hs || []) {
    const existing =
      (_normEmail(h.email) && byEmail.get(_normEmail(h.email))) ||
      (_normName(h.name) && byName.get(_normName(h.name))) ||
      null
    if (existing) {
      // Fill only what the imported row is missing — never clobber.
      if (!existing.email && !existing.apollo_email) existing.email = h.email || ''
      if (!existing.role) existing.role = h.job_title || ''
      if (!existing.phone) existing.phone = h.phone || h.mobile || ''
      existing.hubspot_url = existing.hubspot_url || h.hubspot_url || ''
      existing.lifecycle_stage = existing.lifecycle_stage || h.lifecycle_stage || ''
      existing._hs = true
    } else {
      const added = {
        name: h.name || '',
        role: h.job_title || '',
        phone: h.phone || h.mobile || '',
        email: h.email || '',
        hubspot_url: h.hubspot_url || '',
        lifecycle_stage: h.lifecycle_stage || '',
        _hs: true,
        _hsOnly: true,
      }
      out.push(added)
      index(added)
    }
  }
  return out
}

// The HubSpot match+detail fetch, shared so the CRM panel and the merged
// Contacts card read ONE lookup instead of firing the match twice.
function useHubSpotView(company, contactEmail) {
  const [view, setView] = useState(null)
  const lookupKey = `${company?.name || ''}|${company?.website || ''}|${company?.email || ''}|${contactEmail || ''}`
  useEffect(() => {
    let cancelled = false
    setView(null)
    const { lookup, tried } = leadLookup(company, contactEmail)
    Promise.all([hubspotMatch(lookup), hubspotStatus()])
      .then(async ([match, status]) => {
        if (cancelled) return
        const verdict = matchVerdict(match)
        if (verdict.state === 'unavailable' || verdict.state === 'unconfigured') return
        if (!verdict.confident) {
          setView({ kind: 'nomatch', verdict, match, status, tried })
          return
        }
        const detail = await hubspotCompany({ id: match.company_id })
        if (cancelled) return
        if (!detail.available || !detail.matched || !detail.company) {
          setView({
            kind: 'nomatch',
            verdict: { ...verdict, state: 'none', confident: false },
            match,
            status,
            tried,
          })
          return
        }
        setView({ kind: 'matched', verdict, match, status, detail })
      })
      .catch(() => {
        if (!cancelled) setView(null)
      })
    return () => {
      cancelled = true
    }
    // lookupKey is derived from exactly the fields the lookup is built from.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lookupKey])
  return view
}

// `projects` packs `project_name=…; project_location=…; …` groups.
function parseProjects(raw) {
  const out = []
  let cur = null
  for (const tok of String(raw || '').split(';')) {
    const eq = tok.indexOf('=')
    if (eq === -1) continue
    const key = tok.slice(0, eq).trim()
    const val = tok.slice(eq + 1).trim()
    if (key === 'project_name') {
      cur = {}
      out.push(cur)
      cur.name = val
    } else if (cur) {
      cur[key.replace(/^project_/, '')] = val
    }
  }
  return out.filter((p) => p.name)
}

// Section card, experiment-page style: a roomy semibold title INSIDE the card.
function Card({ title, accent, children, className = '' }) {
  return (
    <section
      className={`relative overflow-hidden rounded-[12px] border border-line bg-surface shadow-card ${className}`}
    >
      {accent && <span className={`absolute bottom-0 left-0 top-0 w-[3px] ${accent}`} aria-hidden="true" />}
      <div className="p-5">
        {title && <h2 className="m-0 mb-4 text-[16px] font-bold tracking-[-0.01em] text-ink">{title}</h2>}
        {children}
      </div>
    </section>
  )
}

// Overview-rail row: small label, value below, hairline between rows.
function RailField({ label, children }) {
  return (
    <div className="py-3 [&:not(:last-child)]:[border-bottom:1px_solid_var(--table-line)]">
      <div className="text-[12.5px] font-medium text-faint">{label}</div>
      <div className="mt-1 text-[14px] leading-[1.5] text-ink">{children}</div>
    </div>
  )
}

function Badge({ children }) {
  return (
    <span className="inline-block whitespace-nowrap rounded-[6px] border border-line bg-panel px-[9px] py-[3px] text-[12px] text-dim">
      {children}
    </span>
  )
}

function CopyGlyph({ className }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
      {/* coolicons: line/Copy.svg */}
      <path d="M9 9V6.2002C9 5.08009 9 4.51962 9.21799 4.0918C9.40973 3.71547 9.71547 3.40973 10.0918 3.21799C10.5196 3 11.0801 3 12.2002 3H17.8002C18.9203 3 19.4801 3 19.9079 3.21799C20.2842 3.40973 20.5905 3.71547 20.7822 4.0918C21.0002 4.51962 21.0002 5.07967 21.0002 6.19978V11.7998C21.0002 12.9199 21.0002 13.48 20.7822 13.9078C20.5905 14.2841 20.2839 14.5905 19.9076 14.7822C19.4802 15 18.921 15 17.8031 15H15M15 15L15 12.1969C15 11.079 15 10.5192 14.7822 10.0918C14.5905 9.71547 14.2842 9.40973 13.9079 9.21799C13.4801 9 12.9203 9 11.8002 9H9H6.2002C5.08009 9 4.51962 9 4.0918 9.21799C3.71547 9.40973 3.40973 9.71547 3.21799 10.0918C3 10.5196 3 11.0801 3 12.2002V17.8002C3 18.9203 3 19.4801 3.21799 19.9079C3.40973 20.2842 3.71547 20.5905 4.0918 20.7822C4.5192 21 5.07899 21 6.19691 21H11.8036C12.9215 21 13.4805 21 13.9079 20.7822C14.2842 20.5905 14.5905 20.2839 14.7822 19.9076C15 19.4802 15 18.921 15 17.8031V15Z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

// A value with a copy-to-clipboard affordance (Feature-Flag style).
function Copyable({ text, children }) {
  const [copied, setCopied] = useState(false)
  return (
    <span className="inline-flex min-w-0 items-center gap-x-1.5">
      <span className="min-w-0 truncate">{children}</span>
      <button
        type="button"
        aria-label="Copy"
        title={copied ? 'Copied' : 'Copy'}
        onClick={() => {
          navigator.clipboard?.writeText(text).then(() => {
            setCopied(true)
            setTimeout(() => setCopied(false), 1200)
          })
        }}
        className="flex-none border-none bg-transparent p-0 text-faint transition-colors hover:text-ink"
      >
        {copied ? (
          <svg viewBox="0 0 24 24" fill="none" className="h-[14px] w-[14px] text-good" aria-hidden="true">
            <path d="M6 12L10.2426 16.2426L18.727 7.75732" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        ) : (
          <CopyGlyph className="h-[14px] w-[14px]" />
        )}
      </button>
    </span>
  )
}

function formatNoteTime(iso) {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

const NOTE_BOX =
  'w-full flex-1 resize-y rounded-[8px] border-none bg-transparent p-0 text-[14px] leading-[1.6] text-ink placeholder:text-faint focus:outline-none disabled:cursor-not-allowed disabled:opacity-60'

// A sticky note: soft panel tile, no outline, gentle lift. Fixed narrow width
// so notes read like post-its instead of stretching across the pane.
const NOTE_TILE =
  'flex min-h-[220px] flex-col rounded-[14px] border-none bg-surface p-[18px] shadow-[0_1px_2px_rgba(0,0,0,0.07),0_8px_20px_-10px_rgba(0,0,0,0.25)] transition-[width] duration-200'

// Working notes on the lead — any number, newest first, edit/delete in place.
function NotesCard({ company, category }) {
  const { user } = useAuth()
  const { can } = useRbac()
  const allowed = can('notes.manage')
  const [, refresh] = useReducer((x) => x + 1, 0)
  const [draft, setDraft] = useState('')
  const [composing, setComposing] = useState(false)
  const [editingId, setEditingId] = useState(null)
  const [editDraft, setEditDraft] = useState('')
  const notes = (getIntel(company)?.notes || []).slice().reverse()

  function submitNew() {
    if (!allowed) return
    if (!addNote(company, category, draft, { email: user?.email, name: user?.name })) return
    setDraft('')
    setComposing(false)
    refresh()
  }
  function saveEdit() {
    updateNote(company, editingId, editDraft)
    setEditingId(null)
    refresh()
  }
  function remove(note) {
    if (!window.confirm('Delete this note?')) return
    deleteNote(company, note.id)
    if (editingId === note.id) setEditingId(null)
    refresh()
  }
  const onCmdEnter = (fn) => (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault()
      fn()
    }
  }

  return (
    <section>
      <h2 className="m-0 mb-3 text-[16px] font-bold tracking-[-0.01em] text-ink">
        Notes{notes.length ? ` (${notes.length})` : ''}
      </h2>

      {/* Sticky-note tiles: each note is its own small card, never a full-width
          row, and the composer sits after the last one. */}
      <div className="flex flex-wrap gap-3">
        {notes.map((n) => (
          <div
            key={n.id}
            className={`${NOTE_TILE} group ${editingId === n.id ? 'w-[540px]' : 'w-[360px]'}`}
          >
            {editingId === n.id ? (
              <>
                <textarea
                  rows={7}
                  autoFocus
                  value={editDraft}
                  onChange={(e) => setEditDraft(e.target.value)}
                  onKeyDown={onCmdEnter(saveEdit)}
                  className={NOTE_BOX}
                />
                <div className="mt-2 flex justify-end gap-x-2">
                  <button
                    type="button"
                    onClick={() => setEditingId(null)}
                    className="rounded-[8px] border border-line bg-surface px-3 py-[6px] text-[12.5px] font-semibold text-dim transition-colors hover:bg-panel hover:text-ink"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={saveEdit}
                    disabled={!editDraft.trim()}
                    className="rounded-[8px] bg-primary px-3 py-[6px] text-[12.5px] font-semibold text-white transition-colors hover:bg-primary-dark disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    Save
                  </button>
                </div>
              </>
            ) : (
              <>
                <p className="m-0 flex-1 whitespace-pre-wrap break-words text-[14px] leading-[1.6] text-ink">{n.text}</p>
                <div className="mt-3 flex items-end gap-x-2 [border-top:1px_solid_var(--table-line)] pt-2">
                  <div className="min-w-0 flex-1 text-[11.5px] leading-[1.45] text-faint">
                    <span className="font-semibold text-dim">{n.authorName || n.author || 'You'}</span>
                    <br />
                    {formatNoteTime(n.at)}
                    {n.editedAt ? ' · edited' : ''}
                  </div>
                  {allowed && (
                    <div className="flex flex-none items-center gap-x-0.5 opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100">
                      <button
                        type="button"
                        onClick={() => {
                          setEditingId(n.id)
                          setEditDraft(n.text)
                        }}
                        aria-label="Edit note"
                        className="grid h-[26px] w-[26px] place-items-center rounded-[7px] border-none bg-transparent text-faint transition-colors hover:bg-surface hover:text-ink [&_svg]:h-[15px] [&_svg]:w-[15px]"
                      >
                        <PencilIcon />
                      </button>
                      <button
                        type="button"
                        onClick={() => remove(n)}
                        aria-label="Delete note"
                        className="grid h-[26px] w-[26px] place-items-center rounded-[7px] border-none bg-transparent text-faint transition-colors hover:bg-[#fef2f2] hover:text-[#dc2626] dark:hover:bg-[rgba(220,38,38,0.16)] [&_svg]:h-[15px] [&_svg]:w-[15px]"
                      >
                        <TrashIcon />
                      </button>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        ))}

        {/* Composer — collapsed to a note-sized "+ Add note" tile until used. */}
        {composing ? (
          <div className={`${NOTE_TILE} w-[360px]`}>
            <textarea
              rows={7}
              autoFocus
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={onCmdEnter(submitNew)}
              placeholder="Call takeaways, pricing, next steps…"
              disabled={!allowed}
              className={NOTE_BOX}
            />
            <div className="mt-2 flex items-center justify-between gap-x-2">
              <button
                type="button"
                onClick={() => {
                  setComposing(false)
                  setDraft('')
                }}
                className="border-none bg-transparent p-0 text-[12px] text-faint transition-colors hover:text-ink"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={submitNew}
                disabled={!allowed || !draft.trim()}
                className="rounded-[8px] bg-primary px-3 py-[6px] text-[12.5px] font-semibold text-white transition-colors hover:bg-primary-dark disabled:cursor-not-allowed disabled:opacity-60"
              >
                Save note
              </button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setComposing(true)}
            disabled={!allowed}
            title={allowed ? undefined : "Your role can't add notes"}
            className="flex h-[220px] w-[360px] flex-col items-center justify-center gap-y-2 rounded-[14px] bg-transparent text-dim transition-colors [border:1px_dashed_var(--border)] hover:bg-panel hover:text-ink disabled:cursor-not-allowed disabled:opacity-50"
          >
            <PlusIcon className="h-[18px] w-[18px]" />
            <span className="text-[13px] font-semibold">Add note</span>
            {notes.length === 0 && (
              <span className="px-4 text-center text-[11.5px] font-normal leading-[1.4] text-faint">
                Call takeaways, pricing, next steps
              </span>
            )}
          </button>
        )}
      </div>
    </section>
  )
}

// Activity feed from the intel record — only events that actually happened.
function ActivityTab({ intel }) {
  const items = []
  if (intel?.firstSeenAt) items.push({ at: intel.firstSeenAt, label: 'First viewed' })
  if (intel?.enrichedAt) items.push({ at: intel.enrichedAt, label: 'Enriched with AI research' })
  for (const o of intel?.outcomes || []) {
    const label = OUTCOME_TYPES.find((t) => t.value === o.type)?.label || o.type
    items.push({ at: o.at, label: `Outcome logged — ${label}` })
  }
  for (const n of intel?.notes || []) items.push({ at: n.at, label: 'Note added' })
  items.sort((a, b) => (b.at || '').localeCompare(a.at || ''))

  return (
    <Card title="Activity">
      {items.length === 0 ? (
        <div className="rounded-[10px] border border-line bg-panel px-4 py-8 text-center text-[14px] text-dim">
          No activity recorded for this lead yet.
        </div>
      ) : (
        <div className="flex flex-col">
          {items.map((it, i) => (
            <div
              key={`${it.at}-${i}`}
              className="flex items-center gap-x-3 py-3 [&:not(:last-child)]:[border-bottom:1px_solid_var(--table-line)]"
            >
              <span className="h-[7px] w-[7px] flex-none rounded-full bg-primary" />
              <span className="min-w-0 flex-1 text-[14px] text-ink">{it.label}</span>
              <span className="flex-none text-[12.5px] tabular-nums text-faint">{formatNoteTime(it.at)}</span>
            </div>
          ))}
          {intel?.views > 1 && (
            <div className="pt-3 text-[12.5px] text-faint">Viewed {intel.views} times in total.</div>
          )}
        </div>
      )}
    </Card>
  )
}

// ── HubSpot mirror ───────────────────────────────────────────────────────────
// A read-only window onto the portal. Nothing on this page writes to HubSpot —
// the app never has, and the API it reads has no write route to call. When
// something here should change, the rep changes it in HubSpot, and the link on
// every row is the shortest path to doing so.

// A record link. When the portal id isn't configured server-side the mirror
// returns no url, and we render plain text rather than a dead anchor.
function HsLink({ url, children, className = '' }) {
  if (!url) return <span className={className}>{children}</span>
  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      className={`text-primary-dark no-underline hover:underline ${className}`}
    >
      {children}
    </a>
  )
}

// One labelled fact in the company summary.
function HsFact({ label, children }) {
  return (
    <div>
      <div className="text-[11.5px] font-medium uppercase tracking-[0.5px] text-faint">{label}</div>
      <div className="mt-1 text-[14px] leading-[1.5] text-ink">{children || <span className="text-faint">—</span>}</div>
    </div>
  )
}

// Sub-section head inside the HubSpot card: label, count, optional right note.
function HsGroup({ label, count, note, empty, children }) {
  return (
    <div className="pt-4 [&:not(:first-child)]:mt-4 [&:not(:first-child)]:[border-top:1px_solid_var(--table-line)]">
      <div className="mb-1 flex flex-wrap items-baseline gap-x-2">
        <h3 className="m-0 text-[12px] font-bold uppercase tracking-[0.5px] text-dim">
          {label}
          {count ? ` (${count})` : ''}
        </h3>
        {note && <span className="text-[11.5px] text-faint">{note}</span>}
      </div>
      {count ? (
        <div className="flex flex-col">{children}</div>
      ) : (
        <div className="py-2 text-[13px] text-faint">{empty}</div>
      )}
    </div>
  )
}

const HS_ROW =
  'flex flex-wrap items-baseline gap-x-3 gap-y-1 py-2.5 [&:not(:last-child)]:[border-bottom:1px_solid_var(--table-line)]'

const ENGAGEMENT_ICON = {
  note: TextIcon,
  email: MailIcon,
  call: PhoneIcon,
  meeting: CalendarIcon,
}

// How many of each list we print before deferring to HubSpot itself. The mirror
// already caps its response; this is the second cap, so a company with 300
// notes doesn't turn the lead page into a scroll marathon.
const HS_SHOW = { contacts: 6, deals: 6, tasks: 6, tickets: 6, engagements: 8 }

// `where` names the system the link opens — HubSpot for the CRM card, Stripe
// for Payments.
function HsMore({ n, url, where = 'HubSpot' }) {
  if (n <= 0) return null
  return (
    <div className="pt-2 text-[12.5px] text-faint">
      {url ? (
        <>
          +{n} more — <HsLink url={url}>open in {where}</HsLink>
        </>
      ) : (
        `+${n} more in ${where}`
      )}
    </div>
  )
}

// The freshness stamp. A mirror that is quietly behind looks live, so this is
// never hidden: if we don't know when the last sync ran, it says so.
function HsFreshness({ age, syncing }) {
  return (
    <span
      title={
        age.known
          ? `Last successful HubSpot sync: ${formatDayTime(age.at.toISOString())}`
          : 'The mirror has not reported a successful sync time.'
      }
      className={`inline-flex items-center gap-x-1.5 whitespace-nowrap text-[12px] ${
        age.stale ? 'text-warn' : 'text-faint'
      }`}
    >
      <span
        className={`h-[6px] w-[6px] flex-none rounded-full ${age.stale ? 'bg-warn' : 'bg-good'}`}
        aria-hidden="true"
      />
      {age.label}
      {syncing ? ' · first sync still running' : ''}
    </span>
  )
}

// Configured, but we would be guessing. This is the state the whole matcher
// exists to make possible: a near-miss is reported as a near-miss, with the
// exact keys we searched on, and never dressed up as this lead's CRM record.
function HsNoMatch({ verdict, match, tried, age, syncing }) {
  const ambiguous = verdict.state === 'ambiguous'
  const candidates = verdict.candidates || []
  // The single "closest candidate" line is only honest when there IS one. On an
  // ambiguous hit the server returns no company_id at all, and naming one of
  // several would reintroduce exactly the guess this panel exists to refuse —
  // so ambiguity gets the full list below instead.
  const candidateName = ambiguous ? '' : match?.company?.name || ''
  const candidateUrl = ambiguous ? '' : match?.company?.hubspot_url || ''

  const headline = ambiguous
    ? `${verdict.count} HubSpot companies share ${verdict.basis} with this lead, so picking one would be a guess.`
    : verdict.state === 'unconfirmed'
      ? `The only hit came from ${verdict.basis}, which isn't strong enough to call a match.`
      : 'No company in the HubSpot mirror matches this lead.'

  return (
    <Card>
      <div className="mb-3 flex flex-wrap items-center gap-x-3 gap-y-2">
        <h2 className="m-0 text-[16px] font-bold tracking-[-0.01em] text-ink">HubSpot</h2>
        <StatusPill tone="neutral">
          {verdict.state === 'none'
            ? 'not in HubSpot'
            : ambiguous
              ? 'ambiguous — treated as unmatched'
              : 'not confidently matched'}
        </StatusPill>
        <span className="ml-auto">
          <HsFreshness age={age} syncing={syncing} />
        </span>
      </div>

      <p className="m-0 text-[13.5px] leading-[1.6] text-dim">
        This lead did <span className="font-semibold text-ink">not</span> confidently match a HubSpot
        company. {headline} Nothing from the CRM is shown below, because showing the wrong company&rsquo;s
        deals is worse than showing none.
      </p>

      <div className="mt-3 rounded-[10px] border border-line bg-panel px-4 py-3">
        <div className="text-[11.5px] font-medium uppercase tracking-[0.5px] text-faint">
          What we searched on
        </div>
        <div className="mt-2 flex flex-col">
          {tried.map((t) => (
            <div
              key={t.label}
              className="flex flex-wrap items-baseline gap-x-3 py-1.5 [&:not(:last-child)]:[border-bottom:1px_solid_var(--table-line)]"
            >
              <span className="w-[120px] flex-none text-[12.5px] text-faint">{t.label}</span>
              <span className="min-w-0 flex-1 break-all text-[13px] text-ink">
                {t.value || <span className="text-faint">nothing on this lead to search with</span>}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* The tie itself, spelled out. A rep told only "3 companies matched"
          can do nothing with that; the names and links are what let them go
          settle it in HubSpot, which is the only place it can be settled. */}
      {ambiguous && candidates.length > 0 && (
        <div className="mt-3 rounded-[10px] border border-line bg-panel px-4 py-3">
          <div className="text-[11.5px] font-medium uppercase tracking-[0.5px] text-faint">
            Companies that matched equally well
          </div>
          <div className="mt-2 flex flex-col">
            {candidates.map((cand) => (
              <div
                key={cand.id}
                className="flex flex-wrap items-baseline gap-x-3 gap-y-1 py-1.5 [&:not(:last-child)]:[border-bottom:1px_solid_var(--table-line)]"
              >
                <span className="min-w-0 flex-1 text-[13px] font-semibold text-ink">
                  {cand.url ? (
                    <HsLink url={cand.url}>{cand.name || `HubSpot company ${cand.id}`}</HsLink>
                  ) : (
                    cand.name || `HubSpot company ${cand.id}`
                  )}
                </span>
                {cand.domain && (
                  <span className="break-all text-[12.5px] text-faint">{cand.domain}</span>
                )}
              </div>
            ))}
          </div>
          {verdict.count > candidates.length && (
            <div className="pt-2 text-[12px] text-faint">
              +{verdict.count - candidates.length} more not listed.
            </div>
          )}
          <p className="m-0 mt-2.5 text-[12.5px] leading-[1.55] text-faint">
            Open the right one in HubSpot and give this lead a matching website
            domain — this page won&rsquo;t choose for you.
          </p>
        </div>
      )}

      {candidateName && (
        <p className="m-0 mt-3 text-[13px] leading-[1.6] text-dim">
          Closest candidate: <HsLink url={candidateUrl} className="font-semibold">{candidateName}</HsLink>{' '}
          <span className="text-faint">
            — unconfirmed. Open it in HubSpot and decide; this page won&rsquo;t treat it as this lead
            until the match is exact.
          </span>
        </p>
      )}
    </Card>
  )
}

// The matched panel: company summary, contacts, open deals, open tasks, recent
// engagements. Every row carries its link back to the record it mirrors.
function HsMatched({ detail, age, syncing, lastError }) {
  const c = detail.company || {}
  const owner = detail.owner
  const contacts = detail.contacts || []
  const openDeals = (detail.deals || []).filter(isOpenDeal)
  const closedDeals = (detail.deals || []).length - openDeals.length
  const openTasks = (detail.tasks || []).filter(isOpenTask)
  const engagements = detail.engagements || []
  // Support tickets (the Zendesk desk, synced into HubSpot): open ones first,
  // then the most recently touched closed ones.
  const tickets = [...(detail.tickets || [])].sort(
    (a, b) => Number(a.is_closed) - Number(b.is_closed) || String(b.updated_at || '').localeCompare(String(a.updated_at || '')),
  )
  const openTickets = tickets.filter((t) => !t.is_closed).length
  const truncated = detail.truncated || {}

  return (
    <Card>
      <div className={`${lastError ? 'mb-1' : 'mb-4'} flex flex-wrap items-center gap-x-3 gap-y-2`}>
        <h2 className="m-0 text-[16px] font-bold tracking-[-0.01em] text-ink">HubSpot</h2>
        {/* No status dot: the pill's words are the whole message (owner
            request, 2026-10-11). */}
        <StatusPill tone="info">
          {c.lifecycle_stage ? humanizeEnum(c.lifecycle_stage) : 'in CRM'}
        </StatusPill>
        <span className="ml-auto flex flex-wrap items-center gap-x-3 gap-y-1">
          <HsFreshness age={age} syncing={syncing} />
          {c.hubspot_url && (
            <HsLink url={c.hubspot_url} className="text-[12.5px] font-semibold">
              Open in HubSpot ↗
            </HsLink>
          )}
        </span>
      </div>

      {/* The "read-only mirror — matched on …" explanation was taken off on
          request (2026-10-11). A sync error still says so: that one changes
          how the numbers below should be read. */}
      {lastError && (
        <p className="m-0 mb-4 text-[12px] leading-[1.5] text-faint">
          The last sync reported an error, so this may be behind.
        </p>
      )}

      {/* Company summary */}
      <div className="grid grid-cols-2 gap-x-6 gap-y-4 sm:grid-cols-4">
        <HsFact label="Lifecycle stage">{humanizeEnum(c.lifecycle_stage)}</HsFact>
        <HsFact label="Owner">
          {owner ? (
            <span className="flex flex-col">
              <span>{owner.name}</span>
              {owner.email && (
                <a
                  href={`mailto:${owner.email}`}
                  className="break-all text-[12.5px] text-primary-dark no-underline hover:underline"
                >
                  {owner.email}
                </a>
              )}
            </span>
          ) : (
            <span className="text-faint">Unassigned</span>
          )}
        </HsFact>
        <HsFact label="Employees">{formatCount(c.employees)}</HsFact>
        <HsFact label="Annual revenue">{formatAmount(c.annual_revenue)}</HsFact>
      </div>
      {(c.industry || c.lead_status || c.last_activity) && (
        <div className="mt-4 grid grid-cols-2 gap-x-6 gap-y-4 sm:grid-cols-4">
          <HsFact label="Industry">{humanizeEnum(c.industry)}</HsFact>
          <HsFact label="Lead status">{humanizeEnum(c.lead_status)}</HsFact>
          <HsFact label="Last activity">{formatDay(c.last_activity)}</HsFact>
          <HsFact label="Last contacted">{formatDay(c.last_contacted)}</HsFact>
        </div>
      )}

      {/* Contacts */}
      <HsGroup
        label="Contacts"
        count={contacts.length}
        note={truncated.contact ? 'most recent only' : ''}
        empty="No contacts on this company in HubSpot."
      >
        {contacts.slice(0, HS_SHOW.contacts).map((p) => (
          <div key={p.id} className={HS_ROW}>
            <div className="min-w-0 flex-1">
              <HsLink url={p.hubspot_url} className="text-[14px] font-semibold">
                {p.name || 'Unnamed contact'}
              </HsLink>
              <div className="mt-[1px] text-[12.5px] text-faint">
                {[p.job_title, humanizeEnum(p.lifecycle_stage)].filter(Boolean).join(' · ') || '—'}
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-x-3 text-[12.5px]">
              {p.email && (
                <a href={`mailto:${p.email}`} className="break-all text-primary-dark no-underline hover:underline">
                  {p.email}
                </a>
              )}
              {(p.phone || p.mobile) && (
                <a href={`tel:${p.phone || p.mobile}`} className="text-dim no-underline hover:underline">
                  {formatPhone(p.phone || p.mobile)}
                </a>
              )}
            </div>
          </div>
        ))}
        <HsMore n={contacts.length - HS_SHOW.contacts} url={c.hubspot_url} />
      </HsGroup>

      {/* Open deals */}
      <HsGroup
        label="Open deals"
        count={openDeals.length}
        note={closedDeals ? `${closedDeals} closed not shown` : ''}
        empty={
          closedDeals
            ? `No open deals — ${closedDeals} closed deal${closedDeals === 1 ? '' : 's'} on this company.`
            : 'No deals on this company in HubSpot.'
        }
      >
        {openDeals.slice(0, HS_SHOW.deals).map((d) => (
          <div key={d.id} className={HS_ROW}>
            <div className="min-w-0 flex-1">
              <HsLink url={d.hubspot_url} className="text-[14px] font-semibold">
                {d.name || 'Untitled deal'}
              </HsLink>
              <div className="mt-[1px] text-[12.5px] text-faint">
                {humanizeEnum(d.stage) || 'stage not set'}
                {d.owner ? ` · ${d.owner.name}` : ''}
              </div>
            </div>
            <div className="flex flex-none items-baseline gap-x-3">
              <span className="text-[14px] font-semibold tabular-nums text-ink">
                {formatAmount(d.amount) || <span className="text-faint">no amount</span>}
              </span>
              <span className="whitespace-nowrap text-[12.5px] tabular-nums text-dim">
                {formatDay(d.close_date) || 'no close date'}
              </span>
            </div>
          </div>
        ))}
        <HsMore n={openDeals.length - HS_SHOW.deals} url={c.hubspot_url} />
      </HsGroup>

      {/* Open tasks */}
      <HsGroup
        label="Open tasks"
        count={openTasks.length}
        note={truncated.task ? 'most recent only' : ''}
        empty="No open tasks on this company in HubSpot."
      >
        {openTasks.slice(0, HS_SHOW.tasks).map((t) => {
          const overdue = isOverdue(t)
          return (
            <div key={t.id} className={HS_ROW}>
              <CheckSquareIcon className="mt-[3px] h-[14px] w-[14px] flex-none text-faint" />
              <div className="min-w-0 flex-1">
                <HsLink url={t.hubspot_url} className="text-[14px] font-semibold">
                  {t.subject || 'Untitled task'}
                </HsLink>
                <div className="mt-[1px] text-[12.5px] text-faint">
                  {[humanizeEnum(t.status), humanizeEnum(t.priority), t.owner?.name]
                    .filter(Boolean)
                    .join(' · ') || '—'}
                </div>
              </div>
              <span
                className={`whitespace-nowrap text-[12.5px] tabular-nums ${
                  overdue ? 'font-semibold text-danger' : 'text-dim'
                }`}
              >
                {t.due_at ? `${overdue ? 'overdue · ' : 'due '}${formatDay(t.due_at)}` : 'no due date'}
              </span>
            </div>
          )
        })}
        <HsMore n={openTasks.length - HS_SHOW.tasks} url={c.hubspot_url} />
      </HsGroup>

      {/* Support tickets */}
      <HsGroup
        label="Support tickets"
        count={tickets.length}
        note={[openTickets ? `${openTickets} open` : 'none open', truncated.ticket ? 'most recent only' : ''].filter(Boolean).join(' · ')}
        empty="No support tickets on this company in HubSpot."
      >
        {tickets.slice(0, HS_SHOW.tickets).map((t) => (
          <div key={t.id} className={HS_ROW}>
            <div className="min-w-0 flex-1">
              <HsLink url={t.hubspot_url} className="text-[14px] font-semibold">
                {t.subject || 'Untitled ticket'}
              </HsLink>
              <div className="mt-[1px] text-[12.5px] text-faint">
                {[t.stage_label || (t.is_closed ? 'Closed' : 'Open'), humanizeEnum(t.priority), humanizeEnum(t.category), t.owner?.name]
                  .filter(Boolean)
                  .join(' · ')}
              </div>
            </div>
            <div className="flex flex-none items-baseline gap-x-3">
              <StatusPill tone={t.is_closed ? 'neutral' : 'warning'}>{t.is_closed ? 'Closed' : 'Open'}</StatusPill>
              <span className="whitespace-nowrap text-[12.5px] tabular-nums text-dim">
                {t.is_closed && t.closed_at
                  ? `closed ${formatDay(t.closed_at)}`
                  : t.opened_at
                    ? `opened ${formatDay(t.opened_at)}`
                    : ''}
              </span>
            </div>
          </div>
        ))}
        <HsMore n={tickets.length - HS_SHOW.tickets} url={c.hubspot_url} />
      </HsGroup>

      {/* Recent engagements */}
      <HsGroup
        label="Recent engagements"
        count={engagements.length}
        note={truncated.engagement ? 'most recent only' : ''}
        empty="No emails, calls, notes or meetings logged on this company."
      >
        {engagements.slice(0, HS_SHOW.engagements).map((e) => {
          const Icon = ENGAGEMENT_ICON[e.kind] || TextIcon
          return (
            <div key={`${e.kind}-${e.id}`} className="flex items-start gap-x-3 py-2.5 [&:not(:last-child)]:[border-bottom:1px_solid_var(--table-line)]">
              <Icon className="mt-[3px] h-[14px] w-[14px] flex-none text-faint" />
              <div className="min-w-0 flex-1">
                <HsLink url={e.hubspot_url} className="text-[13.5px] font-semibold">
                  {e.subject || `${humanizeEnum(e.kind)} — no subject`}
                </HsLink>
                {e.body && (
                  <div className="mt-[2px] line-clamp-2 text-[12.5px] leading-[1.5] text-dim">
                    {e.body}
                  </div>
                )}
              </div>
              <span className="flex-none whitespace-nowrap text-[12px] tabular-nums text-faint">
                {formatDayTime(e.occurred_at || e.updated_at)}
              </span>
            </div>
          )
        })}
        <HsMore n={engagements.length - HS_SHOW.engagements} url={c.hubspot_url} />
      </HsGroup>
    </Card>
  )
}

// Orchestrates the two reads and decides which of the three outcomes renders:
// nothing at all, the honest near-miss, or the mirror.
//
// Renders NOTHING when HubSpot isn't configured — and equally when the mirror
// API can't be reached, because from the browser those two are the same
// observation and a workspace that has never heard of HubSpot must not be
// shown an error about it. Failing that way keeps the rest of the lead page
// exactly as it was, which is the point.
// Pure renderer — the match itself is fetched by useHubSpotView (shared with
// the Contacts card) and handed in as `view`.
function HubSpotSection({ view }) {
  // Ticks the "synced Xm ago" stamp forward while the page sits open, so a tab
  // left open for an hour doesn't keep claiming the data is a minute old.
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 30000)
    return () => clearInterval(t)
  }, [])

  if (!view) return null

  const status = view.status || {}
  const lastSync = view.detail?.last_sync_at || view.match?.last_sync_at || status.last_sync_at
  const age = syncAge(lastSync, now)
  const syncing = !!status.syncing

  if (view.kind === 'matched') {
    return (
      <HsMatched
        detail={view.detail}
        age={age}
        syncing={syncing}
        lastError={status.last_error}
      />
    )
  }
  return (
    <HsNoMatch
      verdict={view.verdict}
      match={view.match}
      tried={view.tried}
      age={age}
      syncing={syncing}
    />
  )
}

// ── Payments ─────────────────────────────────────────────────────────────────
// What this lead has paid us, live from Stripe (lib/payments.js has the whole
// path), beside the billing state HubSpot holds for its contacts. Stripe is
// matched by the lead's emails and company domain; a lead with neither, and no
// HubSpot billing either, renders nothing.
const PAY_SHOW = { payments: 8, subscriptions: 4, invoices: 4 }

function PaymentsCard({ lookup, hsContacts }) {
  // { status: 'loading' | 'ok' | 'error', data, error }
  const [state, setState] = useState({ status: 'loading', data: null, error: null })
  const askable = lookup.emails.length > 0 || lookup.domains.length > 0
  useEffect(() => {
    if (!askable) return undefined
    let cancelled = false
    setState({ status: 'loading', data: null, error: null })
    fetchLeadPayments(lookup)
      .then((data) => {
        if (!cancelled) setState({ status: 'ok', data, error: null })
      })
      .catch((error) => {
        if (!cancelled) setState({ status: 'error', data: null, error })
      })
    return () => {
      cancelled = true
    }
    // lookup.key is derived from exactly the emails and domains asked for.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lookup.key])

  const billing = useMemo(() => hubspotBilling(hsContacts), [hsContacts])
  if (!askable && !billing.length) return null

  const data = state.data
  const customers = data?.customers || []
  const payments = data?.payments || []
  const subscriptions = data?.subscriptions || []
  const openInvoices = data?.open_invoices || []
  const summary = data ? summarizePayments(data) : null
  const stripeUrl = customers.length === 1 ? customers[0].dashboard_url : ''

  return (
    <Card>
      <div className="mb-4 flex flex-wrap items-center gap-x-3 gap-y-2">
        <h2 className="m-0 text-[16px] font-bold tracking-[-0.01em] text-ink">Payments</h2>
        {summary?.pastDue && <StatusPill tone="danger">Past due</StatusPill>}
        {stripeUrl && (
          <span className="ml-auto">
            <HsLink url={stripeUrl} className="text-[12.5px] font-semibold">
              Open in Stripe ↗
            </HsLink>
          </span>
        )}
      </div>

      {askable && state.status === 'loading' && (
        <div className="py-6 text-center text-[13px] text-dim">Reading Stripe…</div>
      )}
      {askable && state.status === 'error' && (
        <p className="m-0 text-[13px] text-dim">
          {state.error?.status === 401
            ? 'Your session has expired. Sign in again to see payments.'
            : state.error?.message || 'Payments could not be read.'}
        </p>
      )}
      {askable && state.status === 'ok' && customers.length === 0 && (
        <p className="m-0 text-[13px] text-dim">No Stripe customer matches this lead&rsquo;s emails or website.</p>
      )}

      {state.status === 'ok' && customers.length > 0 && (
        <>
          <div className="grid grid-cols-2 gap-x-6 gap-y-4 sm:grid-cols-4">
            <HsFact label="Total paid">
              <span className="font-semibold tabular-nums">{summary.paid}</span>
              <span className="ml-1.5 text-[12.5px] text-dim">
                {[
                  `${summary.paymentCount.toLocaleString()} payment${summary.paymentCount === 1 ? '' : 's'}`,
                  summary.refunded ? `${summary.refunded} refunded` : '',
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </span>
              {summary.failedCount > 0 && <span className="ml-1.5 text-[12.5px] text-danger">{summary.failedCount} failed</span>}
            </HsFact>
            <HsFact label="Outstanding">
              {summary.outstanding ? (
                <span className="font-semibold tabular-nums text-danger">{summary.outstanding}</span>
              ) : (
                <span className="tabular-nums">{formatMoney(0)}</span>
              )}
              {summary.openInvoiceCount > 0 && (
                <span className="ml-1.5 text-[12.5px] text-dim">
                  {summary.openInvoiceCount} unpaid invoice{summary.openInvoiceCount === 1 ? '' : 's'}
                </span>
              )}
            </HsFact>
            <HsFact label="Last payment">
              {summary.lastPaymentAt ? formatDay(summary.lastPaymentAt.toISOString()) : ''}
            </HsFact>
            <HsFact label="Customer since">
              {summary.customerSince ? formatDay(summary.customerSince.toISOString()) : ''}
            </HsFact>
          </div>

          {openInvoices.length > 0 && (
            <HsGroup label="Unpaid invoices" count={openInvoices.length} empty="">
              {openInvoices.slice(0, PAY_SHOW.invoices).map((i) => {
                const st = INVOICE_STATUS[i.status] || { label: i.status, tone: 'warning' }
                const due = fromUnix(i.due_date)
                return (
                  <div key={i.id} className={HS_ROW}>
                    <div className="min-w-0 flex-1">
                      <HsLink url={i.dashboard_url} className="text-[14px] font-semibold">
                        {i.number ? `Invoice ${i.number}` : 'Invoice'}
                      </HsLink>
                      <div className="mt-[1px] text-[12.5px] text-faint">
                        {[
                          due ? `due ${formatDay(due.toISOString())}` : `issued ${formatDay(fromUnix(i.created)?.toISOString())}`,
                          i.attempt_count ? `${i.attempt_count} payment attempt${i.attempt_count === 1 ? '' : 's'}` : '',
                          customers.length > 1 ? i.customer_email : '',
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                      </div>
                    </div>
                    <div className="flex flex-none items-baseline gap-x-3">
                      <span className="text-[14px] font-semibold tabular-nums text-ink">{formatMoney(i.amount_remaining, i.currency)}</span>
                      <StatusPill tone={st.tone}>{st.label}</StatusPill>
                    </div>
                  </div>
                )
              })}
              <HsMore n={openInvoices.length - PAY_SHOW.invoices} url={stripeUrl} where="Stripe" />
            </HsGroup>
          )}

          <HsGroup label="Subscriptions" count={subscriptions.length} empty="No subscriptions in Stripe.">
            {subscriptions.slice(0, PAY_SHOW.subscriptions).map((s) => {
              const st = SUBSCRIPTION_STATUS[s.status] || { label: s.status, tone: 'neutral' }
              return (
                <div key={s.id} className={HS_ROW}>
                  <div className="min-w-0 flex-1">
                    <HsLink url={s.dashboard_url} className="text-[14px] font-semibold">
                      {s.product || 'Subscription'}
                    </HsLink>
                    <div className="mt-[1px] text-[12.5px] text-faint">
                      {[subscriptionPrice(s), s.quantity > 1 ? `${s.quantity} seats` : '', customers.length > 1 ? s.customer_email : '']
                        .filter(Boolean)
                        .join(' · ')}
                    </div>
                  </div>
                  <div className="flex flex-none items-baseline gap-x-3">
                    <StatusPill tone={st.tone}>{st.label}</StatusPill>
                    <span className="whitespace-nowrap text-[12.5px] tabular-nums text-dim">{subscriptionWhen(s)}</span>
                  </div>
                </div>
              )
            })}
            <HsMore n={subscriptions.length - PAY_SHOW.subscriptions} url={stripeUrl} where="Stripe" />
          </HsGroup>

          <HsGroup
            label="Recent payments"
            count={payments.length}
            note={(data.notes || []).join(' ')}
            empty="No payments in Stripe."
          >
            {payments.slice(0, PAY_SHOW.payments).map((p) => {
              const st = PAYMENT_STATUS[p.status] || { label: p.status, tone: 'neutral' }
              return (
                <div key={p.id} className={HS_ROW}>
                  <div className="min-w-0 flex-1">
                    <HsLink url={p.dashboard_url} className="text-[14px] font-semibold tabular-nums">
                      {formatMoney(p.amount, p.currency)}
                    </HsLink>
                    <div className="mt-[1px] text-[12.5px] text-faint">
                      {[
                        p.description,
                        p.card,
                        customers.length > 1 ? p.customer_email : '',
                        p.amount_refunded ? `${formatMoney(p.amount_refunded, p.currency)} refunded` : '',
                        p.failure_message,
                      ]
                        .filter(Boolean)
                        .join(' · ') || '—'}
                    </div>
                  </div>
                  <div className="flex flex-none items-baseline gap-x-3">
                    <StatusPill tone={st.tone}>{st.label}</StatusPill>
                    <span className="whitespace-nowrap text-[12.5px] tabular-nums text-dim">
                      {formatDay(fromUnix(p.created)?.toISOString())}
                    </span>
                  </div>
                </div>
              )
            })}
            <HsMore n={payments.length - PAY_SHOW.payments} url={stripeUrl} where="Stripe" />
          </HsGroup>

          {customers.length > 1 && (
            <HsGroup label="Stripe customers" count={customers.length} empty="">
              {customers.map((c) => (
                <div key={c.id} className={HS_ROW}>
                  <div className="min-w-0 flex-1">
                    <HsLink url={c.dashboard_url} className="text-[13.5px] font-semibold">
                      {c.name || c.email}
                    </HsLink>
                    <div className="mt-[1px] text-[12.5px] text-faint">
                      {[c.name ? c.email : '', c.matched_by === 'email' ? 'matched by email' : 'matched by company domain']
                        .filter(Boolean)
                        .join(' · ')}
                    </div>
                  </div>
                  {c.delinquent && <StatusPill tone="danger">Delinquent</StatusPill>}
                </div>
              ))}
            </HsGroup>
          )}
        </>
      )}

      {billing.length > 0 && (
        <HsGroup label="In HubSpot" count={billing.length} empty="">
          {billing.map((b) => (
            <div key={b.key} className={HS_ROW}>
              <div className="min-w-0 flex-1">
                <HsLink url={b.hubspotUrl} className="text-[14px] font-semibold">
                  {b.name}
                </HsLink>
                {b.email && b.email !== b.name && <div className="mt-[1px] text-[12.5px] text-faint">{b.email}</div>}
              </div>
              <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 text-[12.5px] text-dim">
                {b.paymentLinkStatus && (
                  <span>
                    Payment link: <span className="font-semibold text-ink">{humanizeEnum(b.paymentLinkStatus)}</span>
                    {b.paymentLinkCreatedAt ? ` · ${formatDay(b.paymentLinkCreatedAt)}` : ''}
                  </span>
                )}
                {b.paymentMethodAttached != null && <span>{b.paymentMethodAttached ? 'Card on file' : 'No card on file'}</span>}
                {b.trialEndsAt && <span>Trial ends {formatDay(b.trialEndsAt)}</span>}
                {b.subscriptionEndsAt && <span>Subscription ends {formatDay(b.subscriptionEndsAt)}</span>}
                {b.lastPaymentFailedAt && <span className="text-danger">Payment failed {formatDay(b.lastPaymentFailedAt)}</span>}
              </div>
            </div>
          ))}
        </HsGroup>
      )}
    </Card>
  )
}

// The lead page proper. `company` / `category` are ALREADY RESOLVED by the
// wrapper below — from router state, an intel snapshot, or a lookup in the
// local mirror — so this component always has its row on first render. That
// matters: it has hooks after an early return further down, and a row that
// arrived asynchronously here would change the hook count between renders.
function LeadDetailView({ company, category }) {
  const navigate = useNavigate()
  const { user } = useAuth()
  const { can } = useRbac()
  const [tab, setTab] = useState('details')
  // The thread view swaps in over the detail body (same move the chat's
  // provenance pane makes). Declared up here with the other hooks so the
  // branch that renders it can sit after every hook has run.
  const [threadOpen, setThreadOpen] = useState(false)
  // Re-render when enrichment marks land (panel completes while we're open).
  const [, forceEnriched] = useReducer((x) => x + 1, 0)
  useEffect(() => subscribeEnriched(forceEnriched), [])
  const backLabel = CATEGORY_LABELS[category] || 'Leads'
  const backPath = category ? categoryPath(category) : DEFAULT_CATEGORY_PATH

  // Opening a lead is an engagement signal — record the view (compounds intel).
  useEffect(() => {
    if (company) recordView(company, category)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company?.company_id, company?.profile_link, company?.name])

  // Re-read intel after logging an outcome so the score + stage update in place.
  const [tick, setTick] = useState(0)
  const intel = company ? getIntel(company) : null
  const score = company ? calibratedScore(company, calibration()) : null
  const currentStage = intel?.stage
  function logOutcome(type) {
    if (!can('outcomes.log')) return
    recordOutcome(company, category, type)
    logEvent('outcome_logged', {
      actor: user?.email || '',
      actorName: user?.name || '',
      detail: `${type} — ${company.name}`,
    })
    setTick((t) => t + 1)
  }
  void tick

  if (!company) {
    return (
      <div>
        <div className="mb-4 text-[13px] text-dim">
          <Link to={DEFAULT_CATEGORY_PATH} className="text-dim no-underline hover:text-ink">Leads</Link>
        </div>
        <div className="rounded-[12px] border border-line bg-surface p-10 text-center shadow-card">
          <div className="text-[15px] font-semibold text-ink">This lead isn’t in the directory this browser holds.</div>
          <p className="mx-auto mt-2 max-w-[420px] text-[14px] text-dim">
            The link’s lead key matched no company in the synced directory. Open the leads page (it re-syncs the directory) and pick the lead from its row.
          </p>
          <div className="mt-5 flex items-center justify-center gap-x-3">
            {/* One button per vertical in lib/categories.js. */}
            {CATEGORIES.map((c, i) => (
              <Link
                key={c.key}
                to={categoryPath(c.key)}
                className={
                  i === 0
                    ? 'rounded-[9px] bg-primary px-[14px] py-[8px] text-[13px] font-semibold text-white no-underline transition-colors hover:bg-primary-dark'
                    : 'rounded-[9px] border border-line bg-surface px-[14px] py-[8px] text-[13px] font-semibold text-ink no-underline transition-colors hover:bg-panel'
                }
              >
                {c.plural}
              </Link>
            ))}
          </div>
        </div>
      </div>
    )
  }

  const trades = splitList(company.trade)
  const certs = splitList(company.certifications)
  const areas = splitList(company.service_area)
  const projects = parseProjects(company.projects)
  const hasVolume = company.annual_volume && company.annual_volume !== 'Undisclosed'

  // The imported research columns (score, angles, objections, people, money).
  // The section renders only when the row actually carries at least one.
  const hasSalesIntel = [
    company.score,
    company.score_reasoning,
    company.sales_angles,
    company.objections,
    company.owners,
    company.estimators,
    company.has_estimator,
    company.other_decision_makers,
    company.capital_backing,
    company.commercial_residential,
    company.latest_revenue,
    company.revenue_detail,
    company.direct_mobiles,
  ].some((v) => v != null && String(v).trim() !== '')

  // The lead's client row key — same derivation as the directory table's keyOf.
  const rowKey = company.company_id || company.profile_link || company.name

  // Has this lead been researched at all? Not the same question as "do we hold
  // an AI dossier": imported rows arrive enriched (the Sales intel card below
  // is their research), and this account may have enriched it in another
  // session. subscribeEnriched above repaints when a local mark lands.
  const researched = isRowEnriched(company)

  // The AI dossier: this browser's copy renders instantly; if the research was
  // paid for by a teammate (or on another machine) the API has it.
  const [research, setResearch] = useState(() => getEnrichment(rowKey))
  const [researchLoading, setResearchLoading] = useState(false)
  const [confirmReenrich, setConfirmReenrich] = useState(false)
  useEffect(() => subscribeEnrichment(() => setResearch(getEnrichment(rowKey))), [rowKey])
  useEffect(() => {
    let cancelled = false
    const local = getEnrichment(rowKey)
    setResearch(local)
    if (local || !rowKey) return undefined
    setResearchLoading(true)
    enrichLead(category || '', rowKey)
      .then((d) => {
        if (cancelled) return
        if (d?.found) {
          setResearch({
            result: d.result || {},
            rankedScore: d.rankedScore ?? null,
            reasoning: d.reasoning || '',
            contactsNote: d.contactsNote || '',
            phones: d.phones || {},
            enrichedAt: d.enrichedAt || '',
          })
        }
      })
      .catch(() => {})
      .finally(() => !cancelled && setResearchLoading(false))
    return () => {
      cancelled = true
    }
  }, [rowKey, category])

  // Every Enrich control on this page goes through here: a first run starts
  // immediately, a re-run inside 24h is refused, and a re-run after that asks
  // first — each pass is another paid research cycle.
  const reenrich = reenrichState(rowKey, research, company)
  function startEnrichment() {
    if (!can('leads.enrich') || isEnriching(rowKey)) return
    // A batch may already be in flight from before a reload — re-attach instead
    // of paying for the same research twice (the leads page does the same).
    const active = loadActiveBatch()
    if (active && !enrichmentRunning()) {
      resumeEnrichment(active.batchId)
      return
    }
    // Never decide freshness while the server's copy is still in flight: with
    // `research` still null a teammate's 20-minute-old dossier reads as a first
    // run, so the 24h refusal and the confirm dialog are both skipped.
    if (researchLoading) return
    if (!reenrich.allowed) return
    if (!reenrich.first) {
      setConfirmReenrich(true)
      return
    }
    runEnrichment([company], category)
  }

  // Enrichment rewrites the contacts blob with titles, emails and LinkedIn —
  // prefer it over the raw import.
  const contacts = parseContacts(research?.result?.contacts || company.contacts)

  // One shared HubSpot lookup drives both the CRM panel and the merged Contacts
  // card. The contactEmail seed is the best email we hold for this lead.
  const contactEmail =
    contacts.find((c) => c.apollo_email || c.email)?.apollo_email ||
    contacts.find((c) => c.email)?.email ||
    ''
  const hsView = useHubSpotView(company, contactEmail)
  const hsContacts = hsView?.kind === 'matched' ? hsView.detail?.contacts || [] : []
  // Deep link to the CRM record, only on a confident match.
  const hsCompanyUrl =
    hsView?.kind === 'matched' ? hsView.detail?.company?.hubspot_url || '' : ''
  // The mirrored company id — what the thread view queries the mirror by. Only
  // on a confident match, the same rule the link above follows: a thread built
  // from a guessed company would be somebody else's history.
  const hsThreadId = hsView?.kind === 'matched' ? String(hsView.match?.company_id || '') : ''
  // No confident record — the button becomes the portal's own global search
  // for the company name: an honest "find it" instead of a vanished button or
  // a link at a guess. Same behavior as the chat lead cards. Only when the
  // mirror actually answered (hsView present) and reported its portal id.
  const hsPortalId = String(hsView?.status?.portal_id || '')
  // The objects-list ?query= deep link is the one HubSpot actually honors —
  // the /search/... page ignores its query string.
  const hsSearchUrl =
    !hsCompanyUrl && hsPortalId && company.name
      ? `https://app.hubspot.com/contacts/${encodeURIComponent(hsPortalId)}/objects/0-2/views/all/list?query=${encodeURIComponent(company.name)}`
      : ''
  // The Contacts card shows the lead's own roster with the mirrored HubSpot
  // people folded in (deduped by email then name; imported fields never lost).
  const mergedContacts = useMemo(
    () => mergeContacts(contacts, hsContacts),
    [contacts, hsContacts],
  )
  // Who to ask Stripe about: every email the page holds for this lead (its own
  // roster, the AI-enriched addresses, the mirrored HubSpot people, the row's
  // own email) and its website. Recomputed when the HubSpot people arrive.
  const payLookup = useMemo(
    () =>
      paymentLookup({
        website: company.website,
        emails: [company.email, ...mergedContacts.flatMap((c) => [c.email, c.apollo_email])],
      }),
    [company.website, company.email, mergedContacts],
  )

  // A real pipeline stage earns a chip; "new" does not. Every untouched lead
  // is new, so the label carried no information and just crowded the title.
  const stageChip =
    currentStage && STAGE_TONE[currentStage] ? (
      <StatusPill tone={STAGE_TONE[currentStage]}>
        {currentStage}
      </StatusPill>
    ) : null

  const tier =
    (score?.score ?? 0) >= 70
      ? { label: 'HIGH INTENT', chip: 'bg-[#f0fdf4] text-[#15803d] dark:bg-[rgba(21,128,61,0.18)] dark:text-[#4ade80]', bar: 'bg-[#22c55e]' }
      : (score?.score ?? 0) >= 45
        ? { label: 'WARM', chip: 'bg-[#fefce8] text-[#a16207] dark:bg-[rgba(161,98,7,0.18)] dark:text-[#fbbf24]', bar: 'bg-[#f59e0b]' }
        : { label: 'COLD', chip: 'bg-panel text-dim', bar: 'bg-[color:var(--muted)]' }

  // A rail contact value: the value itself, or the same "N/A" placeholder the
  // rest of the table uses when the lead simply doesn't carry that field.
  const railValue = (present, children) =>
    present ? children : <span className="text-faint">N/A</span>

  // THE THREAD, in place of the detail body. Every hook above has already run,
  // so this branch cannot change the hook order — the reason it sits here and
  // not up beside the state it reads.
  if (threadOpen && hsThreadId) {
    return (
      <LeadTimelineView
        lead={{ name: company.name, key: rowKey, email: company.email }}
        companyId={hsThreadId}
        hubspotUrl={hsCompanyUrl}
        onBack={() => setThreadOpen(false)}
      />
    )
  }

  return (
    <div>
      {/* Breadcrumb */}
      <div className="mb-3 flex items-center gap-x-2 text-[13px] text-dim">
        <span>Leads</span>
        <span className="text-faint">|</span>
        <button
          type="button"
          onClick={() => navigate(backPath)}
          className="border-none bg-transparent p-0 text-[13px] text-dim transition-colors hover:text-ink"
        >
          {backLabel}
        </button>
      </div>

      {/* Title row */}
      <div className="mb-6 flex flex-wrap items-center gap-x-3 gap-y-3">
        <h1 className="m-0 min-w-0 flex-shrink truncate text-[24px] font-bold tracking-[-0.02em] text-ink">
          {company.name}
        </h1>
        {stageChip}
        {/* Provenance, aligned with the directory table: a lead the directory
            resolved to a mirrored company (row._source, attached by the data
            layer and carried here in router state) reads as HubSpot-sourced
            here too. The full HubSpot panel below does its own careful match —
            this badge only mirrors the table's flag, never invents one. */}
        {company._source === 'hubspot' ? (
          <span
            className="inline-flex items-center gap-x-1.5 whitespace-nowrap rounded-full bg-primary-tint px-2.5 py-[3px] text-[12px] font-semibold text-primary-dark"
            title={`Mirrored from your HubSpot CRM${company._hubspot?.company?.name ? ` — ${company._hubspot.company.name}` : ''}`}
          >
            <span className="h-[6px] w-[6px] flex-none rounded-full bg-primary" aria-hidden="true" />
            HubSpot
          </span>
        ) : (
          company._hubspot?.ambiguous && (
            <span
              className="whitespace-nowrap text-[12px] font-medium text-faint"
              title="Several HubSpot companies matched this lead equally well — settle which one in HubSpot."
            >
              multiple HubSpot matches
            </span>
          )
        )}
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={startEnrichment}
            disabled={
              isEnriching(rowKey) || researchLoading || !can('leads.enrich') || !reenrich.allowed
            }
            title={
              !can('leads.enrich')
                ? "Your role can't enrich leads"
                : isEnriching(rowKey)
                  ? 'AI research is running on this company'
                  : researchLoading
                    ? 'Checking whether this lead has already been researched…'
                  : !reenrich.allowed
                    ? reenrich.reason
                    : research
                      ? `Research this company again — the current dossier is ${reenrich.age}, and a re-run costs another research cycle`
                      : researched
                        ? 'This lead already carries research from the import — run the live AI pass on top: web sources, verified contacts and a 0–10 fit score'
                        : 'Run the AI research: web sources, verified contacts and a 0–10 fit score'
            }
            className="inline-flex items-center gap-x-1.5 rounded-[9px] border border-line bg-surface px-[14px] py-[8px] text-[13px] font-semibold text-primary-dark transition-colors hover:bg-primary-tint disabled:cursor-not-allowed disabled:opacity-60"
          >
            <SparklesIcon className="h-4 w-4" />
            {isEnriching(rowKey)
              ? 'Enriching…'
              : research
                ? 'Re-run research'
                : researched
                  ? 'Run AI research'
                  : 'Enrich'}
          </button>
          {(company.email || contacts.some((c) => c.email)) && (
            <button
              type="button"
              onClick={() =>
                openCompose({
                  to: company.email || contacts.find((c) => c.email)?.email || '',
                  company: company.name,
                  city: company.city_state || '',
                  trade: company.trade || '',
                  contactName: company.contact_name || contacts[0]?.name || '',
                })
              }
              title="Write it here and send it through Apollo"
              className="inline-flex items-center gap-x-1.5 rounded-[9px] border border-line bg-surface px-[14px] py-[8px] text-[13px] font-semibold text-ink transition-colors hover:bg-panel"
            >
              <MailIcon className="h-4 w-4" />
              Send email
            </button>
          )}
          {company.phone && (
            <a
              href={`tel:${company.phone}`}
              className="inline-flex items-center gap-x-1.5 rounded-[9px] border border-line bg-surface px-[14px] py-[8px] text-[13px] font-semibold text-ink no-underline transition-colors hover:bg-panel"
            >
              <PhoneIcon className="h-4 w-4" />
              Call
            </a>
          )}
          {company.website && (
            <a
              href={siteUrl(company.website)}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-x-1.5 rounded-[9px] bg-primary px-[14px] py-[8px] text-[13px] font-semibold text-white no-underline transition-colors hover:bg-primary-dark"
            >
              <MarketMapIcon className="h-4 w-4" />
              Visit website
            </a>
          )}
          {/* Straight into the CRM record, but ONLY when this lead actually
              resolved to a mirrored HubSpot company — the same confident,
              unambiguous match the panel below renders. An ambiguous or absent
              match shows nothing rather than a link that lands on the wrong
              company. */}
          {(hsCompanyUrl || hsSearchUrl) && (
            <a
              href={hsCompanyUrl || hsSearchUrl}
              target="_blank"
              rel="noreferrer"
              title={
                hsCompanyUrl
                  ? 'Open this company in HubSpot'
                  : 'No matched record in the mirror — search the portal for this company'
              }
              className="inline-flex items-center gap-x-1.5 rounded-[9px] border border-line bg-surface px-[14px] py-[8px] text-[13px] font-semibold text-ink no-underline transition-colors hover:bg-panel"
            >
              <svg viewBox="0 0 24 24" fill="none" className="h-4 w-4" aria-hidden="true">
                <path
                  d="M10.0002 5H8.2002C7.08009 5 6.51962 5 6.0918 5.21799C5.71547 5.40973 5.40973 5.71547 5.21799 6.0918C5 6.51962 5 7.08009 5 8.2002V15.8002C5 16.9203 5 17.4801 5.21799 17.9079C5.40973 18.2842 5.71547 18.5905 6.0918 18.7822C6.5192 19 7.07899 19 8.19691 19H15.8031C16.921 19 17.48 19 17.9074 18.7822C18.2837 18.5905 18.5905 18.2839 18.7822 17.9076C19 17.4802 19 16.921 19 15.8031V14M15 4H20V9M20 4L13 11"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              {hsCompanyUrl ? 'View in HubSpot' : 'Find in HubSpot'}
            </a>
          )}
          {/* Everything the CRM holds on this company, in the order it
              happened — the same view the follow-up cards open. Only on a
              confident match: without one there is no company to read a
              history from. */}
          {hsThreadId && (
            <button
              type="button"
              onClick={() => setThreadOpen(true)}
              title="Every email, note, call, task and deal on this company, oldest first"
              className="inline-flex cursor-pointer items-center gap-x-1.5 rounded-[9px] border border-line bg-surface px-[14px] py-[8px] text-[13px] font-semibold text-ink transition-colors hover:bg-panel"
            >
              <svg viewBox="0 0 24 24" fill="none" className="h-4 w-4" aria-hidden="true">
                <path
                  d="M12 17H19M5 12H19M5 7H19"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
              View thread
            </button>
          )}
        </div>
      </div>

      {/* Rail + tabbed main column */}
      <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-[330px_1fr]">
        {/* ---- Overview rail ---- */}
        <Card title="Overview">
          <div className="-mt-1 flex flex-col">
            <RailField label="Location">
              {company.city_state || <span className="text-faint">—</span>}
            </RailField>
            <RailField label="Website">
              {railValue(
                company.website,
                <Copyable text={company.website}>
                  <a href={siteUrl(company.website)} target="_blank" rel="noreferrer" className="text-primary-dark no-underline hover:underline">
                    {hostname(company.website)}
                  </a>
                </Copyable>,
              )}
            </RailField>
            <RailField label="Email">
              {railValue(
                company.email,
                <Copyable text={company.email}>
                  <a href={`mailto:${company.email}`} className="break-all text-primary-dark no-underline hover:underline">
                    {company.email}
                  </a>
                </Copyable>,
              )}
            </RailField>
            <RailField label="Phone">
              {railValue(
                company.phone,
                <a href={`tel:${company.phone}`} className="text-primary-dark no-underline hover:underline">
                  {formatPhone(company.phone)}
                </a>,
              )}
            </RailField>
            <RailField label="Industry">
              {trades.length ? (
                <span className="flex flex-wrap gap-1.5">
                  {trades.slice(0, 8).map((t) => (
                    <Badge key={t}>{t}</Badge>
                  ))}
                  {trades.length > 8 && <Badge>+{trades.length - 8}</Badge>}
                </span>
              ) : (
                <span className="text-faint">—</span>
              )}
            </RailField>
            <RailField label="Service area">
              {areas.length ? areas.join(', ') : <span className="text-faint">—</span>}
            </RailField>
            <RailField label="Established">
              {company.established || <span className="text-faint">—</span>}
            </RailField>
            <RailField label="Company size">
              {company.company_size || <span className="text-faint">—</span>}
            </RailField>
            <RailField label="Annual volume">
              {hasVolume ? company.annual_volume : <span className="text-faint">Undisclosed</span>}
            </RailField>
            <RailField label="Full address">
              {company.full_address || company.city_state || <span className="text-faint">—</span>}
            </RailField>
            <RailField label="Certifications">
              {certs.length ? (
                <span className="flex flex-wrap gap-1.5">
                  {certs.map((c) => (
                    <Badge key={c}>{c}</Badge>
                  ))}
                </span>
              ) : (
                <span className="text-faint">None</span>
              )}
            </RailField>
          </div>
        </Card>

        {/* ---- Main column ---- */}
        <div className="min-w-0">
          {/* Tabs */}
          <div className="mb-5 flex gap-x-1 [border-bottom:1px_solid_var(--border)]">
            {[
              { key: 'details', label: 'Details' },
              { key: 'activity', label: 'Activity' },
            ].map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => setTab(t.key)}
                className={`-mb-px border-0 border-b-2 border-solid bg-transparent px-4 py-2.5 text-[14px] font-semibold transition-colors ${
                  tab === t.key
                    ? 'border-b-primary text-primary-dark'
                    : 'border-b-transparent text-dim hover:text-ink'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {tab === 'activity' ? (
            <ActivityTab intel={intel} />
          ) : (
            <div className="flex flex-col gap-5">
              {/* Intent summary — accent bar + tier chip, houses outcome logging */}
              <Card accent={tier.bar}>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                  <h2 className="m-0 text-[16px] font-bold tracking-[-0.01em] text-ink">Intent</h2>
                  <span className={`rounded-[6px] px-2 py-[3px] text-[11px] font-bold tracking-[0.04em] ${tier.chip}`}>
                    {tier.label}
                  </span>
                </div>
                <div className="mt-3 flex flex-wrap items-end gap-x-8 gap-y-4">
                  <div>
                    <div className="text-[26px] font-bold leading-none tracking-[-0.02em] text-ink">
                      {score?.score ?? '—'}
                    </div>
                    <div className="mt-1.5 text-[12.5px] text-faint">
                      {score?.delta ? (
                        <span className={score.delta > 0 ? 'text-good' : 'text-danger'}>
                          {score.delta > 0 ? '+' : ''}
                          {score.delta} calibrated from your outcomes
                        </span>
                      ) : (
                        'baseline — calibrates as outcomes are logged'
                      )}
                    </div>
                  </div>
                  <div className="min-w-[260px] flex-1">
                    <div className="mb-1.5 text-[12.5px] font-medium text-faint">Log an outcome</div>
                    <div className="flex flex-wrap gap-2">
                      {OUTCOME_TYPES.map((o) => {
                        const active = currentStage === o.value
                        return (
                          <button
                            key={o.value}
                            type="button"
                            onClick={() => logOutcome(o.value)}
                            disabled={!can('outcomes.log')}
                            title={can('outcomes.log') ? undefined : "Your role can't log outcomes"}
                            className={`rounded-[9px] border px-[12px] py-[7px] text-[13px] font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-[0.45] ${
                              active
                                ? 'border-primary bg-primary-tint text-primary-dark'
                                : 'border-line bg-surface text-dim hover:bg-panel hover:text-ink'
                            }`}
                          >
                            {o.label}
                          </button>
                        )
                      })}
                    </div>
                    <p className="m-0 mt-2 text-[12px] text-faint">
                      Each outcome sharpens intent scores across every segment.
                    </p>
                  </div>
                </div>
              </Card>

              {/* HubSpot mirror — what the CRM already knows. Sits directly
                  under Intent and above research on purpose: "we already have
                  two open deals here" changes what you do next, and it should
                  land before anyone spends a research cycle. Renders nothing at
                  all when HubSpot isn't connected. */}
              <HubSpotSection view={hsView} />

              {/* Payments — what this lead has paid, live from Stripe, with the
                  billing state HubSpot holds beside it. Under the CRM card:
                  "they already pay us $149.70 a month" belongs next to "two
                  open deals". */}
              <PaymentsCard lookup={payLookup} hsContacts={hsContacts} />

              {/* AI research — the whole payoff of enrichment */}
              {research || researchLoading ? (
                <LeadResearch
                  dossier={research}
                  loading={researchLoading && !research}
                  onRefresh={reenrich.allowed ? startEnrichment : null}
                  refreshNote={reenrich.allowed ? '' : reenrich.reason}
                />
              ) : (
                <ResearchTeaser
                  onRun={startEnrichment}
                  running={isEnriching(rowKey)}
                  researched={researched}
                  disabled={!can('leads.enrich')}
                  note={can('leads.enrich') ? '' : "Your role can't run enrichment"}
                />
              )}

              {/* Sales intel — the imported research columns, shown only when
                  the row carries them (older datasets simply skip the card). */}
              {hasSalesIntel && (
                <Card title="Sales intel">
                  <div className="-mt-1 flex flex-col">
                    {(company.score || company.score_reasoning) && (
                      <RailField label="Score">
                        {company.score && (
                          <span className="font-semibold tabular-nums">{company.score}</span>
                        )}
                        {company.score_reasoning && (
                          <div className={company.score ? 'mt-1 text-[13px] leading-[1.6] text-dim' : ''}>
                            {company.score_reasoning}
                          </div>
                        )}
                      </RailField>
                    )}
                    {company.sales_angles && (
                      <RailField label="Pros"><IntelLines value={company.sales_angles} /></RailField>
                    )}
                    {company.objections && (
                      <RailField label="Objections"><IntelLines value={company.objections} /></RailField>
                    )}
                    {company.owners && (
                      <RailField label="Owners"><IntelLines value={company.owners} /></RailField>
                    )}
                    {(company.estimators || company.has_estimator) && (
                      <RailField label="Estimators">
                        <IntelLines value={company.estimators || company.has_estimator} />
                      </RailField>
                    )}
                    {company.other_decision_makers && (
                      <RailField label="Other decision makers">
                        <IntelLines value={company.other_decision_makers} />
                      </RailField>
                    )}
                    {company.capital_backing && (
                      <RailField label="Capital backing"><IntelLines value={company.capital_backing} /></RailField>
                    )}
                    {company.commercial_residential && (
                      <RailField label="Commercial / Residential">{company.commercial_residential}</RailField>
                    )}
                    {(company.latest_revenue || company.revenue_detail) && (
                      <RailField label="Revenue detail">
                        {company.latest_revenue && (
                          <span className="font-semibold">{company.latest_revenue}</span>
                        )}
                        {company.revenue_detail && (
                          <div className={company.latest_revenue ? 'mt-1 text-[13px] leading-[1.6] text-dim' : ''}>
                            {company.revenue_detail}
                          </div>
                        )}
                      </RailField>
                    )}
                    {company.direct_mobiles && (
                      <RailField label="Direct mobiles">
                        <IntelLines value={company.direct_mobiles} />
                      </RailField>
                    )}
                  </div>
                </Card>
              )}

              {/* Contacts — the lead's own roster with mirrored HubSpot people
                  folded in (deduped by email then name; imported fields kept). */}
              <Card title={`Contacts${mergedContacts.length ? ` (${mergedContacts.length})` : ''}`}>
                {mergedContacts.length === 0 && !company.contact_name ? (
                  <div className="rounded-[10px] border border-line bg-panel px-4 py-8 text-center text-[14px] text-dim">
                    No contacts on file for this company.
                  </div>
                ) : (
                  <div className="flex flex-col">
                    {(mergedContacts.length
                      ? mergedContacts
                      : [{ name: company.contact_name, role: company.contact_role, phone: company.contact_phone }]
                    ).map((c, i) => (
                      <div
                        key={`${c.name}-${i}`}
                        className="flex items-start gap-x-3 py-3 [&:not(:last-child)]:[border-bottom:1px_solid_var(--table-line)]"
                      >
                        <span className="grid h-[34px] w-[34px] flex-none place-items-center rounded-[11px] bg-panel text-[12px] font-semibold text-dim">
                          {initialsOf(c.name)}
                        </span>
                        {c.apollo_email || c.apollo_title || c.apollo_linkedin ? (
                          <EnrichedContact contact={{ ...c, phone: formatPhone(c.phone) }} />
                        ) : (
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-x-2">
                              <span className="text-[14px] font-semibold text-ink">{c.name}</span>
                              {/* A person the mirror confirmed (or contributed). */}
                              {c._hs && (
                                <span
                                  className="inline-flex items-center gap-x-1 rounded-full bg-primary-tint px-2 py-[1px] text-[11px] font-semibold text-primary-dark"
                                  title="This contact is on the matched HubSpot company"
                                >
                                  <span className="h-[5px] w-[5px] flex-none rounded-full bg-primary" aria-hidden="true" />
                                  HubSpot
                                </span>
                              )}
                            </div>
                            {(c.role || c.lifecycle_stage) && (
                              <div className="text-[12.5px] text-faint">
                                {[c.role, humanizeEnum(c.lifecycle_stage)].filter(Boolean).join(' · ')}
                              </div>
                            )}
                            <div className="mt-[2px] flex flex-wrap items-center gap-x-3">
                              {c.email && (
                                <a href={`mailto:${c.email}`} className="break-all text-[12.5px] text-primary-dark no-underline hover:underline">
                                  {c.email}
                                </a>
                              )}
                              {c.phone && (
                                <a href={`tel:${c.phone}`} className="inline-flex items-center gap-x-1 text-[12.5px] text-primary-dark no-underline hover:underline">
                                  <PhoneIcon className="h-[13px] w-[13px]" />
                                  {formatPhone(c.phone)}
                                </a>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </Card>

              {/* Projects */}
              {projects.length > 0 && (
                <Card title={`Recent projects (${projects.length})`}>
                  <div className="flex flex-col">
                    {projects.slice(0, 8).map((p, i) => (
                      <div
                        key={`${p.name}-${i}`}
                        className="flex flex-wrap items-baseline gap-x-3 gap-y-1 py-3 [&:not(:last-child)]:[border-bottom:1px_solid_var(--table-line)]"
                      >
                        <div className="min-w-0 flex-1">
                          <div className="text-[14px] font-semibold text-ink">
                            {p.url ? (
                              <a href={p.url} target="_blank" rel="noreferrer" className="text-ink no-underline hover:text-primary hover:underline">
                                {p.name}
                              </a>
                            ) : (
                              p.name
                            )}
                          </div>
                          <div className="mt-[2px] text-[12.5px] text-faint">
                            {[p.type, p.location].filter(Boolean).join(' · ') || 'N/A'}
                          </div>
                        </div>
                        <div className="flex items-center gap-x-2 text-[12.5px] text-dim">
                          {p.status && <Badge>{p.status}</Badge>}
                          {p.date && <span className="text-faint">{p.date}</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>
              )}

              <NotesCard company={company} category={category} />
            </div>
          )}
        </div>
      </div>

      {/* Re-running is real money — one full research cycle per confirm. */}
      {confirmReenrich && (
        <div
          className="fixed inset-0 z-[120] grid place-items-center bg-[rgba(0,0,0,0.55)] p-6"
          onClick={() => setConfirmReenrich(false)}
        >
          <div
            className="w-full max-w-[440px] rounded-[14px] border border-line bg-surface p-5 shadow-[0_24px_60px_rgba(0,0,0,0.45)]"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label="Re-run AI research"
          >
            <h2 className="m-0 text-[16px] font-bold tracking-[-0.01em] text-ink">
              {research ? 'Re-run the research?' : 'Run the AI research?'}
            </h2>
            {/* Two different truths: we hold a dossier and it gets replaced, or
                the lead was researched elsewhere (imported enriched, or run in
                another session) and this run only ADDS a dossier. */}
            <p className="m-0 mt-2 text-[13.5px] leading-[1.6] text-dim">
              {research ? (
                <>
                  {company.name}&rsquo;s dossier is {reenrich.age}. Re-running researches the
                  company from scratch — four web-search passes, scoring and contact lookup — and
                  costs another full run. The current findings are replaced.
                </>
              ) : (
                <>
                  {company.name} has been researched before, but this workspace doesn&rsquo;t hold
                  an AI dossier for it. Running the research costs a full run — four web-search
                  passes, scoring and contact lookup — and adds a dossier without touching the
                  intel already on the lead.
                </>
              )}
            </p>
            <div className="mt-4 flex items-center justify-end gap-x-2">
              <button
                type="button"
                onClick={() => setConfirmReenrich(false)}
                className="rounded-[9px] border border-line bg-surface px-[14px] py-[8px] text-[13px] font-semibold text-dim transition-colors hover:bg-panel hover:text-ink"
              >
                Keep what we have
              </button>
              <button
                type="button"
                onClick={() => {
                  setConfirmReenrich(false)
                  // Re-check at the moment of spend: the dialog may have been
                  // open while a batch started or the policy changed.
                  if (isEnriching(rowKey) || !reenrichState(rowKey, research, company).allowed) return
                  runEnrichment([company], category)
                }}
                className="inline-flex items-center gap-x-1.5 rounded-[9px] bg-primary px-[14px] py-[8px] text-[13px] font-semibold text-white transition-colors hover:bg-primary-dark"
              >
                <SparklesIcon className="h-4 w-4" />
                {research ? 'Re-run research' : 'Run research'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}


/**
 * Resolve WHICH row this page shows, then render it. Three tiers, cheapest
 * first: the row handed over by the list (router state); a tracked snapshot in
 * the intel store (priority queue / refresh); and, failing both, a LOOKUP in
 * the local mirror of the directory by the URL's key — so a bare
 * /leads/company/<key> (a link in a chat answer, a pasted URL, a fresh tab)
 * opens the lead instead of a "this lead opens from the list" notice. The
 * mirror is the same encrypted IndexedDB copy the leads page renders from;
 * when it is empty (chat opened before the leads page ever synced) it is
 * synced once, so the link works on a fresh browser too.
 */
export default function LeadDetail() {
  const { state } = useLocation()
  const { id } = useParams()
  // DECODED TWICE, ON PURPOSE, AND DEFENSIVELY. The router decodes each path
  // segment itself but re-escapes any '/' it finds (decodePath in
  // @remix-run/router), so a key that is a URL — keyOf falls back to
  // profile_link — arrives as `https:%2F%2Fwww…` and needs this second pass.
  // A key holding a literal '%' ("50% Off Masonry") would make that pass throw
  // URIError and white-screen the page, so a failed decode keeps what the
  // router gave us rather than taking the app down.
  const leadKey = useMemo(() => {
    const raw = id || ''
    try {
      return decodeURIComponent(raw)
    } catch {
      return raw
    }
  }, [id])
  const stored = getIntel(leadKey)
  const { manifest, mode } = useManifest()
  const [looked, setLooked] = useState({ key: '', company: null, category: '', done: false })
  const needLookup = !state?.company && !stored?.company && Boolean(leadKey)
  useEffect(() => {
    if (!needLookup) return undefined
    let alive = true
    ;(async () => {
      const find = async () => {
        for (const cat of CATEGORY_KEYS) {
          try {
            const { rows, stale } = await getCachedCompanies(cat)
            const hit = !stale && (rows || []).find((r) => keyOfRow(r) === leadKey)
            if (hit) return { company: hit, category: cat }
          } catch {
            /* unreadable mirror for this category — try the next */
          }
          try {
            const manual = (listManualLeads(cat) || []).find((r) => keyOfRow(r) === leadKey)
            if (manual) return { company: manual, category: cat }
          } catch {
            /* no manual leads */
          }
        }
        return null
      }
      let found = await find()
      if (!found && manifest) {
        // Empty (or missing-this-row) mirror: one sync, then look again.
        for (const cat of CATEGORY_KEYS) {
          try {
            await syncCategory(cat, manifest, mode, () => {})
          } catch {
            /* offline — the notice says so */
          }
        }
        found = await find()
      }
      if (alive) setLooked({ key: leadKey, company: found?.company || null, category: found?.category || '', done: true })
    })()
    return () => {
      alive = false
    }
  }, [needLookup, leadKey, manifest, mode])
  const lookedUp = looked.key === leadKey ? looked : null
  const company = state?.company || stored?.company || lookedUp?.company || null
  const category = state?.category || stored?.category || lookedUp?.category
  const lookingUp = needLookup && !(lookedUp && lookedUp.done)

  if (!company && lookingUp) {
    return (
      <div>
        <div className="mb-4 text-[13px] text-dim">
          <Link to={DEFAULT_CATEGORY_PATH} className="text-dim no-underline hover:text-ink">Leads</Link>
        </div>
        <div className="flex min-h-[220px] flex-col items-center justify-center gap-y-3 rounded-[12px] border border-line bg-surface shadow-card">
          <span
            className="h-7 w-7 animate-spin rounded-full [border:2px_solid_var(--table-line)] [border-top-color:var(--primary)]"
            role="status"
            aria-label="Loading lead"
          />
          <span className="text-[13px] text-dim">Opening this lead from the directory…</span>
        </div>
      </div>
    )
  }
  // Keyed on the lead so switching between two lead pages remounts the view
  // (its per-lead state — tab, research, notes tick — starts clean).
  //
  // The key also carries WHETHER there is a company, because the view returns
  // early when there isn't and declares more hooks below that return. Today no
  // path flips one mount from absent to present (the spinner holds until the
  // lookup settles), but a future one would hit "rendered fewer hooks than
  // expected" — a white screen. Remounting across that edge makes the shape
  // change harmless instead of fatal.
  return (
    <LeadDetailView
      key={`${leadKey || 'lead'}:${company ? 'found' : 'none'}`}
      company={company}
      category={category}
    />
  )
}
